import { createFileRoute } from "@tanstack/react-router";
import type { PromptState } from "api-types/autoRenders/autoRendersTypes";
import type { ClientRenderStyle } from "api-types/autoRenders/styles/autoRenderStylesResponses";
import type { RenderImageResult } from "api-types/autoRenders/types";
import type { ClientMasterPlanHydrated } from "api-types/masterPlans/masterPlansResponses";
import type { ClientStudioDocHydrated } from "api-types/studioDocs/studioDocResponses";
import type { Doc } from "building-types/doc";
import { BuildingViewerViewMode } from "building-types/geometry/buildingGeometryTypes";
import docMarshal from "doc/docMarshal";
import { removeOrphanedInteriorPaletteOptionSet } from "doc/options/paletteOptionSet";
import { EnvPreset } from "gfx/EnvPreset";
import type { THREEViewerSystem } from "gfx/THREEViewerSystem";
import { downloadResultUrl, getDefaultPromptState } from "lib/auto-renders";
import { renderImage } from "lib/auto-renders/tools/renderImageTool";
import RenderStyleEditorModal from "lib/components/autoRenders/RenderStyleEditorModal";
import SceneZoomControls from "lib/components/builders/communities/plans/showroom/SceneZoomControls";
import Simple3DViewer from "lib/components/design/Simple3DViewer";
import useGenerateBuilding from "lib/components/design/useGenerateBuilding";
import GPURequiredContainer from "lib/components/gfx/GPURequiredContainer";
import {
  addParamsToURL,
  getBuilderAutoRenderStylesURL,
  getMasterPlanHydratedURL,
  getStudioDocURL,
} from "lib/endpointHelpers";
import { getBuilderAutoRenderStyleURL } from "lib/endpointHelpers";
import { useFetch } from "lib/fetcher";
import type { BuildingViewerSceneState } from "lib/house/render/buildingViewerSceneState";
import { defaultSceneState, SiteType } from "lib/house/render/buildingViewerSceneState";
import { pickIdRegistryNew } from "lib/house/render/pickIdRegistryNew";
import { optionChoicesNew } from "lib/options/optionChoicesNew";
import { useAppRouterParams } from "lib/routeHelpers";
import { userCanCreateAutoRenders } from "lib/security/client";
import { useUserContext } from "lib/state/UserContextProvider";
import { deleteWithTimeout } from "lib/utils/fetchAuthHelpers";
import { useEffect, useMemo, useRef, useState } from "react";
import Button from "ui/buttons/Button";
import ConfirmDeleteModal from "ui/components/ConfirmDeleteModal";
import { DropdownMenuAndButton } from "ui/components/DropdownMenu";
import type { DropdownMenuItem } from "ui/components/DropdownMenu";
import Link from "ui/components/Link";
import { useAppNavigate } from "ui/hooks/useAppNavigate";
import withAuthentication from "../../../../../components/withAuthentication";
import styles from "./index.module.scss";

type WorkflowStage = "styling" | "render";
type RenderStatus = "ready" | "in_progress" | "complete";
type AspectRatio = "1:1" | "4:3" | "16:9" | "Custom";
type StylingTab = "templates" | "custom";

const AUTO_RENDERS_SCENE_STATE: BuildingViewerSceneState = {
  ...defaultSceneState,
  siteType: SiteType.FALLBACK_TO_GRASS,
  viewMode: BuildingViewerViewMode.EXTERIOR,
  envPreset: EnvPreset.WHITEBOX,
  exteriorLightProbesEnabled: true,
  selectState: null,
  resetCameraOnLevelChange: true,
  viewFilter: {
    // ...defaultSceneState.viewFilter,
    walls: true,
    roofs: true,
    propAppliances: true,
    columns: true,
    windows: true,
    windowUnits: true,
    doors: true,
    doorUnits: true,
    flooring: true,
    propFixtures: true,
    propFurniture: true,
    propOther: true,
    propFoliage: true,
    porches: true,
    wainscoting: true,
    stairSystems: true,
    railings: true,
    casework: true,
    driveways: true,
    electrical: true,
    elevationAnnotation: true,
    elevationNotes: true,
    siteShapes: true,
    site: true,
    siteShapesDriveway: true,
    siteShapesSiteFill: true,
    siteShapesSiteSubtract: true,
    siteShapesRetainingWall: true,
    siteShapesLandscape: true,
    siteShapesWalkway: true,
    siteShapesContour: true,
    siteShapesRoad: true,
    siteShapesHeightOverrides: true,
    slab: true,
    baseTrimMolding: false,
    crownTrimMolding: false,
    exteriorLightProbes: true,
    interiorLightProbes: false,
  },
};

function AutoRendersWorkflow() {
  const navigate = useAppNavigate();
  const { builderId, itemId } = useAppRouterParams();
  const { hasPermission, builder, enabledFeatures } = useUserContext();
  const canCreateAutoRenders = userCanCreateAutoRenders({ hasPermission, builder, enabledFeatures });

  const [workflowStage, setWorkflowStage] = useState<WorkflowStage>("styling");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [customPromptState, setCustomPromptState] = useState<PromptState>(getDefaultPromptState());
  const [stylingTab, setStylingTab] = useState<StylingTab>("templates");
  const [renderStatus, setRenderStatus] = useState<RenderStatus>("ready");
  const [renderProgress, setRenderProgress] = useState<number>(0);
  const [renderResult, setRenderResult] = useState<RenderImageResult | null>(null);
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>("16:9");
  const [deleteStyleId, setDeleteStyleId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [styleEditorOpen, setStyleEditorOpen] = useState(false);
  const [styleEditorMode, setStyleEditorMode] = useState<"create" | "edit">("create");
  const [styleBeingEdited, setStyleBeingEdited] = useState<ClientRenderStyle | null>(null);

  // Fetch render styles
  const {
    data: renderStyles,
    loading: renderStylesLoading,
    mutate: mutateRenderStyles,
  } = useFetch<ClientRenderStyle[]>(builderId ? getBuilderAutoRenderStylesURL({ builderId }) : "", [builderId]);

  const viewerSystemRef = useRef<THREEViewerSystem | null>(null);

  const pickIdRegistry = useMemo(() => pickIdRegistryNew(), []);

  // Determine if itemId is a master plan or studio file
  const isMasterPlan = itemId?.startsWith("mp_");
  const masterPlanId = isMasterPlan && itemId ? itemId.replace("mp_", "") : null;
  const studioDocId = !isMasterPlan && itemId ? itemId : null;

  const { data: masterPlan } = useFetch<ClientMasterPlanHydrated>(
    masterPlanId && builderId ? getMasterPlanHydratedURL({ builderId, masterPlanId }) : "",
    [builderId, masterPlanId],
  );

  const { data: studioDoc } = useFetch<ClientStudioDocHydrated>(
    studioDocId && builderId
      ? addParamsToURL(getStudioDocURL(studioDocId), { builderId, includePublishedDoc: "true" })
      : "",
    [builderId, studioDocId],
  );

  const [doc, setDoc] = useState<Doc | null>(null);

  useEffect(() => {
    if (masterPlan && studioDoc) {
      const publishedDoc = docMarshal(studioDoc.publishedDoc);
      setDoc({
        ...publishedDoc,
        optionSets: removeOrphanedInteriorPaletteOptionSet(publishedDoc.optionSets),
      });
    } else if (studioDoc && !masterPlan) {
      const publishedDoc = docMarshal(studioDoc.publishedDoc);
      setDoc(publishedDoc);
    }
  }, [masterPlan, studioDoc]);

  const { optionChoices } = doc ? optionChoicesNew(doc) : { optionChoices: {} };
  const { building } = useGenerateBuilding(doc || undefined, optionChoices);

  const getCurrentPromptState = (): PromptState => {
    if (stylingTab === "custom") {
      return customPromptState;
    }

    // For templates tab, find selected style from renderStyles
    if (selectedTemplateId) {
      const selectedStyle = renderStyles?.find((s) => s.id === selectedTemplateId);
      return selectedStyle?.promptState ?? getDefaultPromptState();
    }

    return getDefaultPromptState();
  };

  const currentPromptState = getCurrentPromptState();

  const handleZoom = (direction: "in" | "out"): void => {
    if (viewerSystemRef.current?.viewer?.controls) {
      if (direction === "in") {
        viewerSystemRef.current.viewer.controls.zoomTowardsCentre();
      } else {
        viewerSystemRef.current.viewer.controls.zoomOutFromCentre();
      }
    }
  };

  const handleContinue = async (): Promise<void> => {
    if (!canCreateAutoRenders) {
      return;
    }

    if (workflowStage === "styling") {
      setWorkflowStage("render");
    } else if (workflowStage === "render" && renderStatus === "ready") {
      await handleGenerateRender();
    }
  };

  const handleGenerateRender = async (): Promise<void> => {
    if (!building) {
      return;
    }

    setRenderStatus("in_progress");
    setRenderProgress(0);

    // Simulate progress updates
    const progressInterval = setInterval(() => {
      setRenderProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return prev;
        }
        return prev + Math.random() * 10;
      });
    }, 1000);

    try {
      const itemType = isMasterPlan ? "master_plan" : "studio_file";
      if (!builderId || !itemId) {
        throw new Error("Builder ID, item ID, and item type are required");
      }
      const promptState = currentPromptState;
      const result = await renderImage({
        builderId,
        itemId,
        itemType,
        renderName: building.doc.name,
        promptState,
        aspectRatio,
      });

      if (result.success) {
        setRenderResult(result);
        setRenderStatus("complete");
        setRenderProgress(100);
      } else {
        throw new Error(result.message || "Render failed");
      }
    } catch (error) {
      console.error("Render error:", error);
      setRenderStatus("ready");
      setRenderProgress(0);
    } finally {
      clearInterval(progressInterval);
    }
  };

  const handleBack = (): void => {
    if (workflowStage === "render") {
      setWorkflowStage("styling");
      setRenderStatus("ready");
      setRenderProgress(0);
      setRenderResult(null);
    } else {
      void navigate({ to: `/builders/${builderId}/auto-renders` });
    }
  };

  const handleDownload = (): void => {
    if (renderResult?.autoRender?.resultUrl) {
      downloadResultUrl(renderResult.autoRender.resultUrl, `photoreal-express-${Date.now()}.jpg`);
    }
  };

  const canContinue = (): boolean => {
    if (!canCreateAutoRenders) {
      return false;
    }

    if (workflowStage === "styling") {
      return selectedTemplateId !== null || stylingTab === "custom";
    }
    if (workflowStage === "render") {
      return renderStatus === "ready";
    }
    return false;
  };

  const backHref = `/builders/${builderId}/auto-renders`;

  let renderStylesPageState: "loading" | "loaded" | "none" = "loading";
  if (!renderStylesLoading) {
    if (renderStyles && renderStyles.length > 0) {
      renderStylesPageState = "loaded";
    } else {
      renderStylesPageState = "none";
    }
  }

  let aspectRatioAsFraction: string | undefined;
  switch (aspectRatio) {
    case "1:1":
      aspectRatioAsFraction = "1 / 1";
      break;
    case "4:3":
      aspectRatioAsFraction = "4 / 3";
      break;
    case "16:9":
      aspectRatioAsFraction = "16 / 9";
      break;
    default:
      aspectRatioAsFraction = undefined;
      break;
  }

  return (
    <div className={styles.workflowContainer}>
      {/* Header */}
      <div className={styles.header}>
        <Link href={backHref} className={styles.backLink}>
          &lt; Back
        </Link>
        <div className={styles.headerTitle}>
          <span>AutoRender</span>
        </div>
        <div className={styles.headerRight}>Orion</div>
      </div>

      <div className={styles.mainContent}>
        {/* Left Sidebar */}
        <div className={styles.sidebar}>
          <button
            className={`${styles.sidebarItem} ${workflowStage === "styling" ? styles.sidebarItemActive : ""}`}
            onClick={() => setWorkflowStage("styling")}
          >
            Styling
          </button>
          <button
            className={`${styles.sidebarItem} ${workflowStage === "render" ? styles.sidebarItemActive : ""}`}
            onClick={() => setWorkflowStage("render")}
          >
            Render
          </button>
        </div>

        {/* Main Content Area */}
        <div className={styles.contentArea}>
          {/* Left Panel */}
          <div className={styles.leftPanel}>
            {workflowStage === "styling" && (
              <div className={styles.stylingPanel}>
                <h2 className={styles.panelTitle}>Styling</h2>
                <p className={styles.panelDescription}>
                  Select a style to apply to your render. Options can be reconfigured back on the Lots page.
                </p>

                <div className={styles.tabs}>
                  <button
                    className={`${styles.tab} ${stylingTab === "templates" ? styles.tabActive : ""}`}
                    onClick={() => {
                      setStylingTab("templates");
                    }}
                  >
                    Templates
                  </button>
                  <button
                    className={`${styles.tab} ${stylingTab === "custom" ? styles.tabActive : ""}`}
                    onClick={() => {
                      if (!canCreateAutoRenders) {
                        return;
                      }

                      setStylingTab("custom");
                      setSelectedTemplateId(null);
                      setStyleEditorMode("create");
                      setStyleBeingEdited(null);
                      setStyleEditorOpen(true);
                    }}
                    disabled={!canCreateAutoRenders}
                  >
                    Custom
                  </button>
                </div>

                {stylingTab === "templates" && (
                  <div className={styles.templateList}>
                    {renderStylesPageState === "loading" && <div>Loading templates...</div>}
                    {renderStylesPageState === "loaded" &&
                      renderStyles &&
                      renderStyles.map((style) => {
                        const menuItems: DropdownMenuItem[] = [];

                        // Only show delete option for non-system templates
                        if (canCreateAutoRenders && !style.isSystemTemplate) {
                          menuItems.push(
                            {
                              children: "Delete",
                              onClick: (e) => {
                                e?.stopPropagation();
                                setDeleteStyleId(style.id);
                              },
                            },
                            {
                              children: "Edit",
                              onClick: (e) => {
                                e?.stopPropagation();
                                setStyleEditorMode("edit");
                                setStyleBeingEdited(style);
                                setStyleEditorOpen(true);
                              },
                            },
                          );
                        }

                        return (
                          <div
                            key={style.id}
                            className={`${styles.templateCard} ${
                              selectedTemplateId === style.id ? styles.templateCardSelected : ""
                            }`}
                            style={{ position: "relative" }}
                          >
                            <button
                              className={styles.templateCardButton}
                              onClick={() => setSelectedTemplateId(style.id)}
                            >
                              <div className={styles.templateThumbnail}>
                                {style.thumbnailUrl ? (
                                  <img src={style.thumbnailUrl} alt={style.name} />
                                ) : (
                                  <div className={styles.templateThumbnailPlaceholder}>No thumbnail</div>
                                )}
                              </div>
                              <div className={styles.templateContent}>
                                <div className={styles.templateName}>{style.name}</div>
                                {style.description && (
                                  <div className={styles.templateDescription}>{style.description}</div>
                                )}
                              </div>
                            </button>
                            {menuItems.length > 0 && (
                              <div className={styles.templateCardMenu} onClick={(e) => e.stopPropagation()}>
                                <DropdownMenuAndButton menuItems={menuItems} ellipsisSize={12} />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    {renderStylesPageState === "none" && <div>No templates available.</div>}
                  </div>
                )}

                {stylingTab === "custom" && (
                  <div className={styles.customPanel}>
                    <p>Use the custom styling modal to create reusable render styles with advanced settings.</p>
                    {canCreateAutoRenders ? (
                      <Button
                        btnStyle="primary"
                        btnSize="medium"
                        onClick={() => {
                          setStyleEditorMode("create");
                          setStyleBeingEdited(null);
                          setStyleEditorOpen(true);
                        }}
                      >
                        Open Custom Styling
                      </Button>
                    ) : null}
                  </div>
                )}
              </div>
            )}

            {workflowStage === "render" && (
              <div className={styles.renderPanel}>
                {renderStatus === "ready" && (
                  <>
                    <div className={styles.renderStatusIcon}>▶</div>
                    <h2 className={styles.panelTitle}>Ready to Render</h2>
                    <p className={styles.panelDescription}>
                      AutoRender images are AI generated based on the provided 3D model and desired render Styling.
                      Content should be reviewed prior to distribution.
                    </p>
                  </>
                )}

                {renderStatus === "in_progress" && (
                  <>
                    <h2 className={styles.panelTitle}>Render in Progress</h2>
                    <p className={styles.panelDescription}>
                      AutoRender images are AI generated based on the provided 3D model and desired render Styling.
                      Content should be reviewed prior to distribution.
                    </p>
                  </>
                )}

                {renderStatus === "complete" && renderResult && (
                  <>
                    <div className={`${styles.renderStatusIcon} ${styles.large}`}>✓</div>
                    <h2 className={styles.panelTitle}>Render Complete</h2>
                    <p className={styles.panelDescription}>
                      Saved to your History in <Link href="#">AutoRender</Link>
                    </p>
                    <div className={styles.renderDetails}>
                      <div className={styles.renderDetailRow}>
                        <span className={styles.renderDetailLabel}>EXTERIOR PALETTE:</span>
                        <span className={styles.renderDetailValue}>Standard</span>
                      </div>
                      <div className={styles.renderDetailRow}>
                        <span className={styles.renderDetailLabel}>GARAGE:</span>
                        <span className={styles.renderDetailValue}>Three Car Garage</span>
                      </div>
                      <div className={styles.renderDetailRow}>
                        <span className={styles.renderDetailLabel}>OWNER&apos;S SUITE EXTERIOR DOORS:</span>
                        <span className={styles.renderDetailValue}>No Exterior Door</span>
                      </div>
                      <div className={styles.renderDetailRow}>
                        <span className={styles.renderDetailLabel}>STYLING:</span>
                        <span className={styles.renderDetailValue}>
                          {(() => {
                            if (selectedTemplateId) {
                              const selectedStyle = renderStyles?.find((s) => s.id === selectedTemplateId);
                              return selectedStyle?.name ?? "Custom";
                            }

                            return "Custom";
                          })()}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Right Panel - 3D Viewer */}
          <div className={styles.rightPanel}>
            <div
              className={styles.viewerContainer}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#2d2d2d",
              }}
            >
              {renderStatus === "in_progress" && (
                <div className={styles.renderPreview}>
                  <div className={styles.renderPreviewBlur}>
                    {renderResult?.autoRender?.screenshotUrl && (
                      <img src={renderResult.autoRender.screenshotUrl} alt="Render preview" />
                    )}
                  </div>
                  <div className={styles.renderProgress}>
                    <div className={styles.renderProgressCircle}>{Math.round(renderProgress)}%</div>
                  </div>
                </div>
              )}

              {renderStatus === "complete" && renderResult && renderResult.autoRender?.resultUrl && (
                <div className={styles.renderResult}>
                  <img src={renderResult.autoRender?.resultUrl} alt="Generated render" />
                  <div className={styles.renderResultBadge}>AI Generated</div>
                </div>
              )}

              {renderStatus !== "in_progress" && renderStatus !== "complete" && (
                <GPURequiredContainer>
                  <div
                    className={styles.viewerWrapper}
                    style={{
                      width: "100%",
                      maxWidth: "100%",
                      height: "auto",
                      ...(aspectRatioAsFraction ? { aspectRatio: aspectRatioAsFraction } : {}),
                    }}
                  >
                    <SceneZoomControls handleZoom={handleZoom} />
                    <Simple3DViewer
                      building={building}
                      pickIdRegistry={pickIdRegistry}
                      updateEnabled
                      viewerSys={viewerSystemRef}
                      initialSceneState={AUTO_RENDERS_SCENE_STATE}
                      showCameraPanel
                    />
                  </div>
                </GPURequiredContainer>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <Button
          btnStyle="secondary"
          onClick={() => {
            if (workflowStage === "render" && renderStatus === "complete") {
              setWorkflowStage("styling");
              setRenderStatus("ready");
              setRenderProgress(0);
              setRenderResult(null);
              setSelectedTemplateId(null);
              setCustomPromptState(getDefaultPromptState());
              setStylingTab("templates");
            } else {
              handleBack();
            }
          }}
        >
          {workflowStage === "render" && renderStatus === "complete" ? "Start Over" : "Back"}
        </Button>

        {workflowStage === "render" && renderStatus === "complete" ? (
          <>
            <Button btnStyle="primary" onClick={handleDownload}>
              Download
            </Button>
            <div className={styles.footerRight}>
              <p className={styles.footerText}>
                For bespoke Photoreal rendering services, including 3D walkthroughs and interior renderings, connect
                with <Link href="#">Higharc Photoreal</Link>.
              </p>
            </div>
          </>
        ) : (
          <>
            <Button btnStyle="primary" onClick={handleContinue} disabled={!canContinue()}>
              Continue
            </Button>
            <div className={styles.aspectRatioControls}>
              <span className={styles.aspectRatioLabel}>Aspect Ratio</span>
              {(["1:1", "4:3", "16:9", "Custom"] as AspectRatio[]).map((ratio) => (
                <button
                  key={ratio}
                  className={`${styles.aspectRatioButton} ${
                    aspectRatio === ratio ? styles.aspectRatioButtonActive : ""
                  }`}
                  onClick={() => setAspectRatio(ratio)}
                >
                  {ratio}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {canCreateAutoRenders && deleteStyleId && builderId && (
        <ConfirmDeleteModal
          onClose={() => {
            setDeleteStyleId(null);
            setIsDeleting(false);
          }}
          onConfirm={async (e) => {
            e.preventDefault();
            if (!deleteStyleId || !builderId) {
              return;
            }

            setIsDeleting(true);
            try {
              const url = getBuilderAutoRenderStyleURL({ builderId, styleId: deleteStyleId });
              const res = await deleteWithTimeout(url, null, 30000);

              if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.message || "Failed to delete style");
              }

              // Remove the deleted style from the list
              if (renderStyles) {
                void mutateRenderStyles(renderStyles.filter((s) => s.id !== deleteStyleId));
              }

              // Clear selection if the deleted style was selected
              if (selectedTemplateId === deleteStyleId) {
                setSelectedTemplateId(null);
              }

              setDeleteStyleId(null);
            } catch (error) {
              console.error("Failed to delete style:", error);
              // Error is handled by global error handling
            } finally {
              setIsDeleting(false);
            }
          }}
          entityType="Render Style"
          entityName={renderStyles?.find((s) => s.id === deleteStyleId)?.name}
          message="Are you sure you want to delete this render style? This action cannot be undone."
          isDeleting={isDeleting}
          requireEntityNameEntry={false}
        />
      )}
      {canCreateAutoRenders && builderId && (
        <RenderStyleEditorModal
          open={styleEditorOpen}
          onClose={() => setStyleEditorOpen(false)}
          builderId={builderId}
          mode={styleEditorMode}
          style={styleBeingEdited ?? undefined}
          onSaved={(savedStyle) => {
            if (renderStyles) {
              const existingIndex = renderStyles.findIndex((s) => s.id === savedStyle.id);
              if (existingIndex >= 0) {
                const next = [...renderStyles];
                next[existingIndex] = savedStyle;
                void mutateRenderStyles(next);
              } else {
                void mutateRenderStyles([...renderStyles, savedStyle]);
              }
            }
          }}
        />
      )}
    </div>
  );
}

export const Route = createFileRoute("/builders/$builder_id/auto-renders/$item_id/")({
  component: withAuthentication(AutoRendersWorkflow, {
    permissions: ["VIEW_AUTO_RENDERS"],
    features: ["autoRenders"],
    products: ["autoRenders"],
  }),
});
